import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Link2, ChevronDown, ChevronRight, ClipboardCopy } from 'lucide-react';
import { toast } from 'sonner';
import api, { enablebankingAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

function formatDate(value) {
  return value ? new Date(value).toLocaleString('en-GB') : '-';
}


function AccountRow({ account, entities, onSave }) {
  const [selectedEntity, setSelectedEntity] = useState(account.entity_uuid || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!selectedEntity) {
      toast.error('Select an entity before saving');
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('account_id', account.id);
      formData.append('entity_uuid', selectedEntity);

      await api.post('/admin/integrations/enablebanking/account-mapping', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Mapped ${account.iban || account.id} to entity`);
      if (onSave) onSave();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const isMapped = !!account.entity_uuid;

  return (
    <tr className="border-b last:border-0">
      <td className="px-3 py-2 text-xs">{account.display_label || account.account_name || account.iban || '-'}</td>
      <td className="px-3 py-2 text-xs">{account.currency || '-'}</td>
      <td className="px-3 py-2 text-xs">{account.psu_type || '-'}</td>
      <td className="px-3 py-2">
        {isMapped ? (
          <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-[10px]">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            mapped
          </Badge>
        ) : (
          <Badge variant="destructive" className="text-[10px]">
            <AlertTriangle className="h-3 w-3 mr-1" />
            unmapped
          </Badge>
        )}
      </td>
      <td className="px-3 py-2">
        {account.hidden ? (
          <Badge variant="outline" className="text-[10px] text-muted-foreground">hidden</Badge>
        ) : null}
      </td>
      <td className="px-3 py-2">
        <select
          className="border rounded-md px-2 py-1 text-xs bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring min-w-[160px]"
          value={selectedEntity}
          onChange={(e) => setSelectedEntity(e.target.value)}
        >
          <option value="">-- select entity --</option>
          {entities.map((ent) => (
            <option key={ent.id} value={ent.id} disabled={ent.resolvable === false}>
              {ent.name || ent.id}{ent.resolvable === false ? ' (not in canonical registry)' : ''}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleSave}
          disabled={saving || !selectedEntity}
          className="text-xs h-7"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Save
        </Button>
      </td>
    </tr>
  );
}


function SupersededRow({ account }) {
  return (
    <tr className="border-b last:border-0 text-muted-foreground">
      <td className="px-3 py-2 text-xs">{account.display_label || account.account_name || account.iban || '-'}</td>
      <td className="px-3 py-2 text-xs">{account.currency || '-'}</td>
      <td className="px-3 py-2 text-xs">{account.psu_type || '-'}</td>
      <td className="px-3 py-2">
        <Badge variant="outline" className="text-[10px] text-muted-foreground">superseded</Badge>
      </td>
      <td className="px-3 py-2" colSpan={2}>
        prior-session duplicate, same IBAN mapped under the current session
      </td>
      <td className="px-3 py-2">
        <Button size="sm" variant="outline" disabled className="text-xs h-7">
          Save
        </Button>
      </td>
    </tr>
  );
}


export function EBAccountMappingPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [supersededOpen, setSupersededOpen] = useState(false);
  const [orphanDiag, setOrphanDiag] = useState(null);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [orphanError, setOrphanError] = useState(null);
  const [continuityPreview, setContinuityPreview] = useState(null);
  const [continuityLoading, setContinuityLoading] = useState(false);
  const [continuityApplying, setContinuityApplying] = useState(false);
  const [continuityError, setContinuityError] = useState(null);
  const [continuityApplyResult, setContinuityApplyResult] = useState(null);

  const fetchContinuityPreview = async () => {
    setContinuityLoading(true);
    setContinuityError(null);
    try {
      const { data: d } = await enablebankingAPI.continuityRepointPreview();
      setContinuityPreview(d);
    } catch (err) {
      setContinuityError(getErrorMessage(err));
    } finally {
      setContinuityLoading(false);
    }
  };

  const runContinuityPreview = async () => {
    setContinuityApplyResult(null);
    await fetchContinuityPreview();
  };

  const applyContinuityRepoint = async () => {
    setContinuityApplying(true);
    setContinuityError(null);
    try {
      const { data: d } = await enablebankingAPI.continuityRepointApply();
      setContinuityApplyResult(d);
      await fetchContinuityPreview();
      await fetchMapping();
    } catch (err) {
      setContinuityError(getErrorMessage(err));
    } finally {
      setContinuityApplying(false);
    }
  };

  const runOrphanDiagnostic = async () => {
    setOrphanLoading(true);
    setOrphanError(null);
    try {
      const { data: d } = await api.get('/admin/diagnostics/eb-orphaned-accounts');
      setOrphanDiag(d);
    } catch (err) {
      setOrphanError(getErrorMessage(err));
    } finally {
      setOrphanLoading(false);
    }
  };

  const copyOrphanDiag = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(orphanDiag, null, 2));
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Could not copy: ' + getErrorMessage(err));
    }
  };

  const fetchMapping = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await api.get('/admin/diagnostics/eb-account-mapping');
      setData(d);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMapping(); }, [fetchMapping]);

  const accounts = data?.accounts || [];
  const entities = data?.entities || [];
  const supersededAccounts = accounts.filter((a) => a.superseded);
  const visibleAccounts = accounts.filter((a) => !a.superseded);
  // Backend is the source of truth for counts (it excludes superseded
  // prior-session duplicates from unmapped -- see services/eb_account_relink.py).
  const mapped = data?.mapped_count ?? visibleAccounts.filter((a) => !!a.entity_uuid).length;
  const unmapped = data?.unmapped_count ?? (visibleAccounts.length - mapped);
  const supersededCount = data?.superseded_count ?? supersededAccounts.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Link2 className="h-5 w-5" />
          Enable Banking Account Mapping
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Map Enable Banking accounts to internal entities.
          Each account must be assigned to an entity before transactions can be ingested.
        </p>

        {/* Loading */}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading account mapping...
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Content */}
        {!loading && data && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-xs font-medium">{mapped} mapped</span>
              </div>
              {unmapped > 0 && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <span className="text-xs font-medium text-amber-700">{unmapped} unmapped</span>
                </div>
              )}
              {unmapped === 0 && visibleAccounts.length > 0 && (
                <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-[10px]">
                  All accounts mapped
                </Badge>
              )}
            </div>

            {/* Accounts table */}
            {visibleAccounts.length > 0 ? (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-1.5 font-medium">Account</th>
                      <th className="text-left px-3 py-1.5 font-medium">Currency</th>
                      <th className="text-left px-3 py-1.5 font-medium">PSU Type</th>
                      <th className="text-left px-3 py-1.5 font-medium">Status</th>
                      <th className="text-left px-3 py-1.5 font-medium">Hidden</th>
                      <th className="text-left px-3 py-1.5 font-medium">Entity</th>
                      <th className="text-left px-3 py-1.5 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleAccounts.map((account) => (
                      <AccountRow
                        key={account.id || account.iban}
                        account={account}
                        entities={entities}
                        onSave={fetchMapping}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <XCircle className="h-4 w-4" />
                No Enable Banking accounts found.
              </div>
            )}

            {/* Superseded sessions (collapsed): prior-session duplicates a
                reconsent orphaned, display-classified out of "unmapped".
                Never deleted -- history may still reference them. */}
            {supersededCount > 0 && (
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => setSupersededOpen((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/30"
                >
                  {supersededOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  Superseded sessions ({supersededCount})
                </button>
                {supersededOpen && (
                  <div className="overflow-x-auto border-t">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-3 py-1.5 font-medium">Account</th>
                          <th className="text-left px-3 py-1.5 font-medium">Currency</th>
                          <th className="text-left px-3 py-1.5 font-medium">PSU Type</th>
                          <th className="text-left px-3 py-1.5 font-medium">Status</th>
                          <th className="text-left px-3 py-1.5 font-medium" colSpan={2}>Reason</th>
                          <th className="text-left px-3 py-1.5 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {supersededAccounts.map((account) => (
                          <SupersededRow key={account.id || account.iban} account={account} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Refresh button */}
        <Button
          variant="outline"
          size="sm"
          onClick={fetchMapping}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          Refresh
        </Button>

        {/* Orphaned-row diagnostic (routes/admin_enablebanking.py::
            eb_orphaned_accounts_diagnostic): per-row classification of every
            unmapped account, so an operator doesn't have to guess why a row
            wasn't auto-relinked. */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={runOrphanDiagnostic}
              disabled={orphanLoading}
              data-testid="eb-orphan-diagnostic-button"
            >
              {orphanLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Orphan diagnostic
            </Button>
            {orphanDiag && !orphanError && (
              <Button
                variant="outline"
                size="sm"
                onClick={copyOrphanDiag}
                data-testid="eb-orphan-diagnostic-copy"
              >
                <ClipboardCopy className="h-3.5 w-3.5 mr-1.5" />
                Copy result
              </Button>
            )}
          </div>

          {orphanError && (
            <div className="flex items-center gap-2 text-xs text-red-500" data-testid="eb-orphan-diagnostic-error">
              <AlertTriangle className="h-3.5 w-3.5" />
              {orphanError}
            </div>
          )}

          {orphanDiag && !orphanError && (
            <div className="space-y-2" data-testid="eb-orphan-diagnostic-result">
              <p className="text-xs text-muted-foreground">
                {orphanDiag.unmapped_total ?? 0} unmapped row(s) ·
                {' '}{orphanDiag.superseded_count ?? 0} superseded ·
                {' '}{orphanDiag.genuinely_unmapped_count ?? 0} genuinely unmapped
              </p>
              {(orphanDiag.rows || []).length === 0 ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <XCircle className="h-3.5 w-3.5" />
                  No unmapped Enable Banking accounts.
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1 font-medium">Account</th>
                        <th className="text-left px-2 py-1 font-medium">Session ID</th>
                        <th className="text-left px-2 py-1 font-medium">EB uid</th>
                        <th className="text-left px-2 py-1 font-medium">IBAN</th>
                        <th className="text-left px-2 py-1 font-medium">Created</th>
                        <th className="text-left px-2 py-1 font-medium">Last successful fetch</th>
                        <th className="text-left px-2 py-1 font-medium">Ever ingested</th>
                        <th className="text-left px-2 py-1 font-medium">Verdict</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphanDiag.rows.map((row) => (
                        <tr key={row.account_id} className="border-b last:border-0 align-top">
                          <td className="px-2 py-1 font-mono">{row.display_label || row.account_name || row.account_id}</td>
                          <td className="px-2 py-1 font-mono">{row.eb_session_id || '-'}</td>
                          <td className="px-2 py-1 font-mono">{row.eb_account_uid || '-'}</td>
                          <td className="px-2 py-1 font-mono">{row.iban || 'no IBAN'}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{formatDate(row.created_at)}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{formatDate(row.last_successful_fetch)}</td>
                          <td className="px-2 py-1">{row.ever_ingested_transaction_count ?? 0}</td>
                          <td className="px-2 py-1 min-w-[280px]">{row.verdict}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Continuity repoint (services/eb_continuity_repoint.py): finds an
            entity-mapped-but-stale account and a live-but-unmapped account
            that are the SAME real bank account across separate connections
            (a reconsent-shaped relink only looks within one connection_id).
            Preview never writes; Apply only ever touches auto_relinkable
            (strong-identity) pairs, never an ambiguous one. */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={runContinuityPreview}
              disabled={continuityLoading}
              data-testid="eb-continuity-preview-button"
            >
              {continuityLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Continuity repoint: Preview
            </Button>
            {continuityPreview && !continuityError && continuityPreview.auto_relinkable_count > 0 && (
              <Button
                variant="default"
                size="sm"
                onClick={applyContinuityRepoint}
                disabled={continuityApplying}
                data-testid="eb-continuity-apply-button"
              >
                {continuityApplying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                Apply ({continuityPreview.auto_relinkable_count})
              </Button>
            )}
          </div>

          {continuityError && (
            <div className="flex items-center gap-2 text-xs text-red-500" data-testid="eb-continuity-error">
              <AlertTriangle className="h-3.5 w-3.5" />
              {continuityError}
            </div>
          )}

          {continuityApplyResult && !continuityError && (
            <div className="flex items-center gap-2 text-xs text-green-700" data-testid="eb-continuity-apply-result">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Repointed {continuityApplyResult.repointed_count}, {continuityApplyResult.ambiguous_count} still ambiguous.
            </div>
          )}

          {continuityPreview && !continuityError && (
            <div className="space-y-2" data-testid="eb-continuity-preview-result">
              <p className="text-xs text-muted-foreground">
                {continuityPreview.mapped_stale_count ?? 0} mapped-stale ·
                {' '}{continuityPreview.live_unmapped_count ?? 0} live-unmapped ·
                {' '}{continuityPreview.backfill_recovered ?? 0}/{continuityPreview.backfill_attempted ?? 0} IBAN(s) recovered ·
                {' '}{continuityPreview.auto_relinkable_count ?? 0} auto-relinkable ·
                {' '}{continuityPreview.ambiguous_count ?? 0} ambiguous
              </p>

              {(continuityPreview.auto_relinkable || []).length === 0 && (continuityPreview.ambiguous || []).length === 0 ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <XCircle className="h-3.5 w-3.5" />
                  No continuity twin pairs found.
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1 font-medium">Live account</th>
                        <th className="text-left px-2 py-1 font-medium">Decision</th>
                        <th className="text-left px-2 py-1 font-medium">Matched stale account</th>
                        <th className="text-left px-2 py-1 font-medium">Confidence</th>
                        <th className="text-left px-2 py-1 font-medium">Proposed currency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...(continuityPreview.auto_relinkable || []), ...(continuityPreview.ambiguous || [])].map((row) => (
                        <tr key={row.live_account_id} className="border-b last:border-0 align-top">
                          <td className="px-2 py-1 font-mono">{row.live_account_name || row.live_account_id}</td>
                          <td className="px-2 py-1">
                            {row.decision === 'auto_relink' ? (
                              <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-[10px]">auto-relinkable</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] text-amber-700">ambiguous</Badge>
                            )}
                          </td>
                          <td className="px-2 py-1 font-mono">
                            {row.target_account_id || (row.candidates || []).map((c) => c.account_name || c.id).join(', ') || '-'}
                          </td>
                          <td className="px-2 py-1 font-mono">{row.confidence || (row.candidates || [])[0]?.confidence || '-'}</td>
                          <td className="px-2 py-1">{row.proposed_currency ? `${row.proposed_currency} (${row.proposed_currency_source})` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
